/**
 * Hides the variants we can't ship to the shopper's state, and shows the notice when none of a
 * product's variants can go there.
 *
 * Liquid can't do this on its own: the state comes from an IP lookup cached in localStorage
 * (assets/user-location.js), so the server renders every option value and tags the ones that are
 * unreachable in a given state with `data-restricted-states`. This module turns that plus the
 * resolved state into one injected stylesheet keyed on option value id.
 *
 * A stylesheet rather than per-element `hidden`, because variant-picker.js re-renders the whole
 * picker from the server on every selection — anything written onto an element is undone by the
 * next morph, whereas the rules keep applying with no flash. Option value ids are stable and
 * shop-wide unique, so one stylesheet covers every picker on the page.
 */

import { getLocation, LOCATION_CHANGE_EVENT } from '@theme/user-location';

const STYLE_ID = 'shipping-restriction-rules';

/** Enough re-selections to walk down a three-option product and stop, rather than loop. */
const MAX_RESELECTS = 6;

/** @type {Record<string, string>} */
const STATE_NAMES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'Washington, D.C.',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

let reselects = 0;
let scheduled = false;

/** @returns {string} The shopper's state abbreviation, or '' when we don't know it. */
const currentState = () => (getLocation().state || '').trim().toUpperCase();

/**
 * @param {string | undefined} list - A space-separated state list from the markup.
 * @returns {string[]}
 */
const parseStates = (list) => (list || '').split(/\s+/).filter(Boolean);

/**
 * The option values with no shippable variant behind them in `state`.
 * @param {string} state
 * @returns {Set<string>}
 */
function blockedOptionValueIds(state) {
  /** @type {Set<string>} */
  const ids = new Set();
  if (!state) return ids;

  for (const input of document.querySelectorAll('[data-restricted-states]')) {
    if (!(input instanceof HTMLElement)) continue;
    if (!parseStates(input.dataset.restrictedStates).includes(state)) continue;

    // Ids go straight into a selector below, so only ever trust digits.
    const id = (input.dataset.optionValueId || '').replace(/\D/g, '');
    if (id) ids.add(id);
  }

  return ids;
}

/**
 * @param {Set<string>} optionValueIds - Option values with nowhere to ship.
 * @param {Set<string>} blockedProductIds - Products with no shippable variant at all.
 */
function writeRules(optionValueIds, blockedProductIds) {
  let style = document.getElementById(STYLE_ID);

  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.append(style);
  }

  // The label covers both the option buttons and the "Pick a Stand" chips, which select a
  // Planter value from outside its fieldset and so carry the same option value id.
  const selectors = Array.from(optionValueIds).flatMap((id) => [
    `.variant-option__button-label:has(input[data-option-value-id="${id}"])`,
    `.variant-option__select option[data-option-value-id="${id}"]`,
  ]);

  // The in-column buy buttons are a sibling of the notice and go with a CSS rule shipped
  // alongside it. The sticky bar isn't — it's rendered by the section, outside the block — so it's
  // matched by product id instead, which also keeps a featured-product section elsewhere on the
  // page from taking the main product's bar down with it.
  for (const id of blockedProductIds) {
    selectors.push(`sticky-add-to-cart[data-product-id="${id}"]`);
  }

  const rules = selectors.length ? `${selectors.join(',\n')} {\n  display: none;\n}` : '';
  if (style.textContent !== rules) style.textContent = rules;
}

/**
 * Moves off a selection the shopper can't be shipped, by picking the option's first reachable
 * value. Clicking is deliberate — it goes through variant-picker.js, so the price, gallery and
 * buy buttons all follow, and the re-render cascades the options downstream of it.
 *
 * One click per pass: the re-render triggers another pass, which handles the next option.
 *
 * @param {Set<string>} blocked
 * @returns {boolean} True when a re-selection was made.
 */
function reselectBlockedSelection(blocked) {
  if (!blocked.size || reselects >= MAX_RESELECTS) return false;

  for (const picker of document.querySelectorAll('variant-picker[data-template-product-match="true"]')) {
    for (const fieldset of picker.querySelectorAll('fieldset[data-fieldset-index]')) {
      const checked = fieldset.querySelector('input:checked');
      if (!(checked instanceof HTMLInputElement)) continue;
      if (!blocked.has(checked.dataset.optionValueId ?? '')) continue;

      const alternative = Array.from(fieldset.querySelectorAll('input')).find(
        (input) => input instanceof HTMLInputElement && !blocked.has(input.dataset.optionValueId ?? '')
      );

      if (alternative instanceof HTMLInputElement) {
        reselects += 1;
        alternative.click();
        return true;
      }
    }
  }

  return false;
}

/**
 * @param {string} state
 * @returns {Set<string>} The ids of the products the notice is now showing for.
 */
function paintNotices(state) {
  const stateName = STATE_NAMES[state] ?? state;

  /** @type {Set<string>} */
  const blockedProductIds = new Set();

  for (const notice of document.querySelectorAll('shipping-restriction')) {
    if (!(notice instanceof HTMLElement)) continue;

    const blocked = Boolean(state) && parseStates(notice.dataset.blockedStates).includes(state);

    for (const slot of notice.querySelectorAll('[data-shipping-restriction-state]')) {
      if (slot.textContent !== stateName) slot.textContent = stateName;
    }

    if (notice.hidden !== !blocked) notice.hidden = !blocked;

    // Ids go straight into a selector, so only ever trust digits.
    const productId = (notice.dataset.productId || '').replace(/\D/g, '');
    if (blocked && productId) blockedProductIds.add(productId);
  }

  return blockedProductIds;
}

function apply() {
  const state = currentState();
  const blocked = blockedOptionValueIds(state);

  // Notices first: the buy buttons they hide are keyed off which ones end up visible.
  const blockedProductIds = paintNotices(state);

  writeRules(blocked, blockedProductIds);
  reselectBlockedSelection(blocked);
}

/** Coalesces the burst of mutations a picker morph produces into one pass. */
function schedule() {
  if (scheduled) return;
  scheduled = true;

  requestAnimationFrame(() => {
    scheduled = false;
    apply();
  });
}

// Every selection re-renders the picker from the server, which brings back the option values we
// hid and can bring in values that weren't available before. Watching the subtree covers both,
// and every path that re-renders it — including the intermediate ones that dispatch no event.
const observer = new MutationObserver(schedule);

function observePickers() {
  for (const picker of document.querySelectorAll('variant-picker')) {
    observer.observe(picker, { childList: true, subtree: true });
  }
}

document.addEventListener(LOCATION_CHANGE_EVENT, () => {
  // A fresh location is a fresh budget: the shopper moving states should be able to move the
  // selection again, even if an earlier state already spent the allowance.
  reselects = 0;
  schedule();
});

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      observePickers();
      apply();
    },
    { once: true }
  );
} else {
  observePickers();
  apply();
}
